/**
 * Literal formatting
 *
 * Locale-aware display text for typed RDF literals (dates, booleans,
 * numbers), shared by the technical property table and the friendly
 * entity view widgets.
 */

import type { EntityObject } from '@/types';

const XSD_NS = 'http://www.w3.org/2001/XMLSchema#';
const XSD_ANY_URI = `${XSD_NS}anyURI`;
const XSD_DATE_TIME = `${XSD_NS}dateTime`;
const XSD_DATE = `${XSD_NS}date`;
const XSD_BOOLEAN = `${XSD_NS}boolean`;
const XSD_NUMERIC = new Set([`${XSD_NS}decimal`, `${XSD_NS}double`, `${XSD_NS}float`, `${XSD_NS}integer`]);

export function isUriValued(obj: EntityObject): boolean {
  return obj.type === 'uri' || (obj.type === 'literal' && obj.datatype === XSD_ANY_URI);
}

export function formatLiteralValue(
  obj: EntityObject,
  locale: string,
  yesLabel: string,
  noLabel: string
): string | null {
  if (!obj.datatype) return null;

  if (obj.datatype === XSD_DATE_TIME || obj.datatype === XSD_DATE) {
    const date = new Date(obj.value);
    if (isNaN(date.getTime())) return null;

    return obj.datatype === XSD_DATE
      ? new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          timeZone: 'UTC',
        }).format(date)
      : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  }

  if (obj.datatype === XSD_BOOLEAN) {
    return obj.value === 'true' ? yesLabel : noLabel;
  }

  if (XSD_NUMERIC.has(obj.datatype)) {
    const num = Number(obj.value);
    if (isNaN(num)) return null;
    return new Intl.NumberFormat(locale).format(num);
  }

  return null;
}
